/**
 * Navigation utility functions for handling task navigation with new tab support
 */

/**
 * Navigate to a URL, supporting new tab opening via Ctrl/Cmd+click or middle mouse button
 * @param {string} url - The URL to navigate to
 * @param {MouseEvent} event - The mouse event (click or mousedown)
 * @param {Object} options - Additional options
 * @param {Function} options.beforeNavigate - Optional callback to run before navigation (e.g., storing state)
 */
export function navigateToUrl(url, event, options = {}) {
  const { beforeNavigate } = options

  // Check if user wants to open in new tab (Ctrl/Cmd+click)
  const isNewTab = event.ctrlKey || event.metaKey

  // Run beforeNavigate callback if provided
  if (beforeNavigate) {
    try {
      beforeNavigate()
    } catch (e) {
      console.warn('Error in beforeNavigate callback:', e)
    }
  }

  // Navigate to URL
  if (isNewTab) {
    // Open in new tab
    window.open(url, '_blank')
  } else {
    // Navigate in same window
    window.location.href = url
  }
}

/**
 * Handle middle mouse button clicks (button 1) for opening links in new tabs
 * @param {string} url - The URL to navigate to
 * @param {MouseEvent} event - The mousedown event
 * @param {Object} options - Additional options
 * @param {Function} options.beforeNavigate - Optional callback to run before navigation
 * @param {Function} options.shouldNavigate - Optional function to check if navigation should proceed
 */
export function handleMiddleMouseClick(url, event, options = {}) {
  const { beforeNavigate, shouldNavigate } = options

  // Only handle middle mouse button (button 1)
  if (event.button === 1) {
    // Prevent default middle-click behavior (scroll)
    event.preventDefault()

    // Check if navigation should proceed
    if (shouldNavigate && !shouldNavigate(event)) {
      return
    }

    // Run beforeNavigate callback if provided
    if (beforeNavigate) {
      try {
        beforeNavigate()
      } catch (e) {
        console.warn('Error in beforeNavigate callback:', e)
      }
    }

    // Open in new tab
    window.open(url, '_blank')
  }
}

/**
 * Build a task URL with optional query parameters
 * @param {string} projectSegment - The project URL segment (code or ID)
 * @param {string} taskSegment - The task URL segment (iid)
 * @param {Object} params - Optional query parameters
 * @param {string} params.chatId - Telegram chat ID
 * @param {string} params.fromId - Telegram from ID
 * @param {string} params.boardId - Board ID (optional)
 * @returns {string} The complete task URL
 */
export function buildTaskUrl(projectSegment, taskSegment, params = {}) {
  const { chatId, fromId, boardId } = params
  const url = new URL(`/projects/${projectSegment}/tasks/${taskSegment}`, window.location.origin)

  if (chatId) url.searchParams.set('chat_id', chatId)
  if (fromId) url.searchParams.set('from_id', fromId)
  if (boardId) url.searchParams.set('board_id', boardId)

  return url.toString()
}
